import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Filter, 
  PackageX, 
  AlertTriangle,
  Calendar,
  DollarSign,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  PackagePlus,
  ArrowUpDown,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { MerchandiseMovementsTable } from './components/MerchandiseMovementsTable';
import { MerchandiseMovementsStats } from './components/MerchandiseMovementsStats';
import { MerchandiseMovementFormModal } from './components/MerchandiseMovementFormModal';
import { MerchandiseMovementApprovalModal } from './components/MerchandiseMovementApprovalModal';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { toast } from 'sonner';
import { useMerchandiseMovements } from './hooks/useMerchandiseMovements';
import { useComboData } from './hooks/useComboData';
import { useAuth } from '@/context/AuthContext';
import type {
  MerchandiseMovement,
  MerchandiseMovementFormData,
  MerchandiseMovementFilters,
  MovementDirection
} from '@/types/merchandise-movement.types';

export const MerchandiseMovementsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // Check if user is CAJERO (positionId: 3)
  const isCajero = user?.positionId === 3;

  const {
    merchandiseMovements,
    stats,
    filters,
    loading,
    error,
    updateFilters,
    fetchMovements,
    createMerchandiseMovement,
    approveMerchandiseMovement,
    rejectMerchandiseMovement,
    cancelMerchandiseMovement
  } = useMerchandiseMovements();

  const {
    comboData,
    loading: comboLoading,
    error: comboError
  } = useComboData();

  const [selectedMovement, setSelectedMovement] = useState<MerchandiseMovement | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmMovement, setConfirmMovement] = useState<MerchandiseMovement | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelMovement, setCancelMovement] = useState<MerchandiseMovement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDirection, setSelectedDirection] = useState<MovementDirection | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>(filters.status || 'all');

  // Detectar parámetro de URL para abrir modal automáticamente
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'new-exit') {
      setSelectedDirection('exit');
      setShowFormModal(true);
      // Limpiar el parámetro de la URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('action');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    // Don't update filters immediately on text input to prevent crashes
    // The search will be applied when the "Buscar" button is clicked
  };

  const handleSearchClick = () => {
    fetchMovements();
  };

  const handleFilterChange = (key: keyof MerchandiseMovementFilters, value: string) => {
    updateFilters({ [key]: value });
  };

  const handleStatusFilterChange = (status: string) => {
    setSelectedStatus(status);
    updateFilters({ status: status === 'all' ? 'all' : status });
    fetchMovements({ status: status === 'all' ? 'all' : status });
  };

  // Status filter options
  const statusFilters: Array<{ key: string, label: string, activeBg: string, borderColor: string, countColor: string }> = [
    { key: 'all', label: 'Todos', activeBg: 'bg-gray-700 text-white', borderColor: 'border-l-gray-400', countColor: 'text-gray-500' },
    { key: 'pending_approval', label: 'Pendientes', activeBg: 'bg-amber-500 text-white', borderColor: 'border-l-amber-500', countColor: 'text-amber-600' },
    { key: 'approved', label: 'Aprobados', activeBg: 'bg-emerald-500 text-white', borderColor: 'border-l-emerald-500', countColor: 'text-emerald-600' },
    { key: 'rejected', label: 'Rechazados', activeBg: 'bg-rose-500 text-white', borderColor: 'border-l-rose-500', countColor: 'text-rose-600' },
    { key: 'cancelled', label: 'Cancelados', activeBg: 'bg-gray-500 text-white', borderColor: 'border-l-gray-400', countColor: 'text-gray-500' }
  ];

  // Get count for each status from stats API
  const getStatusCount = (status: string) => {
    if (!stats) return 0;

    switch (status) {
      case 'all':
        return stats.total;
      case 'pending_approval':
        return stats.pendingApproval;
      case 'approved':
        return stats.approved;
      case 'rejected':
        return stats.rejected;
      case 'cancelled':
        return merchandiseMovements.filter(m => m.status === 'cancelled').length;
      default:
        return 0;
    }
  };

  const handleCreateMovement = async (movementData: MerchandiseMovementFormData) => {
    const success = await createMerchandiseMovement(movementData);
    if (success) {
      setShowFormModal(false);
    }
  };

  const handleApproveClick = (movement: MerchandiseMovement) => {
    setConfirmMovement(movement);
    setShowConfirmDialog(true);
  };

  const confirmApproval = async () => {
    if (confirmMovement) {
      const success = await approveMerchandiseMovement(confirmMovement.id);
      if (success) {
        toast.success(`${confirmMovement.direction === 'entry' ? 'Ingreso' : 'Salida'} ${confirmMovement.movementNumber} aprobado correctamente`, {
          description: 'El inventario ha sido actualizado automáticamente'
        });
      }
      setConfirmMovement(null);
    }
  };

  const handleRejectClick = (movement: MerchandiseMovement) => {
    setSelectedMovement(movement);
    setShowApprovalModal(true);
  };

  const handleRejection = async (reason: string) => {
    if (selectedMovement) {
      const success = await rejectMerchandiseMovement(selectedMovement.id, reason);

      if (success) {
        setShowApprovalModal(false);
        setSelectedMovement(null);
      }
    }
  };

  const handleCancelClick = (movement: MerchandiseMovement) => {
    setCancelMovement(movement);
    setShowCancelModal(true);
  };

  const handleCancellation = async (reason: string) => {
    if (cancelMovement) {
      const success = await cancelMerchandiseMovement(cancelMovement.id, reason);

      if (success) {
        const wasApproved = cancelMovement.status === 'approved';
        toast.success(`${cancelMovement.direction === 'entry' ? 'Ingreso' : 'Salida'} ${cancelMovement.movementNumber} cancelado correctamente`, {
          description: wasApproved ? 'El inventario ha sido revertido automáticamente' : undefined
        });
        setShowCancelModal(false);
        setCancelMovement(null);
      }
    }
  };

  const clearFilters = () => {
    updateFilters({
      direction: 'all',
      locationId: 'all',
      ingredientId: 'all',
      movementType: 'all',
      status: 'all',
      dateFrom: '',
      dateTo: '',
      requestedBy: ''
    });
    setSearchTerm('');
    setSelectedDirection('all');
  };

  if (error || comboError) {
    return (
      <div>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="h-5 w-5" />
              <span>
                {error && `Error al cargar movimientos de mercadería: ${error}`}
                {error && comboError && ' | '}
                {comboError && `Error al cargar datos de combos: ${comboError}`}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">Movimientos de Mercadería</h1>
            {merchandiseMovements.length > 0 && (
              <Badge variant="secondary" className="text-sm">
                {merchandiseMovements.length} {merchandiseMovements.length === 1 ? 'movimiento' : 'movimientos'}
              </Badge>
            )}
          </div>
          <p className="text-gray-600">
            Registro y control de ingresos y salidas de mercadería
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            onClick={() => {
              setSelectedDirection('entry');
              setShowFormModal(true);
            }} 
            className="w-fit"
            variant="outline"
          >
            <TrendingUp className="mr-2 h-4 w-4" />
            Nuevo Ingreso
          </Button>
          <Button 
            onClick={() => {
              setSelectedDirection('exit');
              setShowFormModal(true);
            }} 
            className="w-fit"
          >
            <TrendingDown className="mr-2 h-4 w-4" />
            Nueva Salida
          </Button>
        </div>
      </div>


      {/* Filtros - Hidden for CAJERO */}
      {!isCajero && (
        <Card>
          <CardContent className="pt-4 pb-3 space-y-2">
            {/* Fila 1: Filtros de busqueda */}
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs font-medium text-gray-600 block mb-1">Insumo:</label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 h-3.5 w-3.5" />
                  <Input
                    type="text"
                    placeholder="Buscar insumo..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-8 h-9 text-sm"
                    disabled={comboLoading}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Sucursal:</label>
                <Select
                  value={filters.locationId}
                  onValueChange={(value) => handleFilterChange('locationId', value)}
                  disabled={comboLoading}
                >
                  <SelectTrigger className="h-9 w-48 text-sm">
                    <SelectValue placeholder={comboLoading ? "Cargando..." : "Todas"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las sucursales</SelectItem>
                    {comboData.locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Desde:</label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="text-sm h-9 w-40"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Hasta:</label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="text-sm h-9 w-40"
                />
              </div>
              <Button
                size="sm"
                className="h-9 bg-macartur-primary hover:bg-macartur-primary/90"
                onClick={handleSearchClick}
              >
                <Search className="mr-1 h-4 w-4" />
                Buscar
              </Button>
            </div>
            {/* Fila 2: Chips de estado */}
            <div className="flex flex-wrap items-center gap-1.5">
              {statusFilters.map((sf) => {
                const count = getStatusCount(sf.key);
                const isActive = selectedStatus === sf.key;
                return (
                  <button
                    key={sf.key}
                    onClick={() => handleStatusFilterChange(sf.key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      isActive
                        ? sf.activeBg
                        : `bg-white border border-gray-200 border-l-[3px] ${sf.borderColor} text-gray-700 hover:bg-gray-50`
                    }`}
                  >
                    {sf.label}
                    <span className={`text-xs font-bold ${isActive ? 'text-white' : sf.countColor}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabla de movimientos */}
      <Card>
        <CardHeader>
          <CardTitle>Movimientos ({searchTerm.trim()
            ? merchandiseMovements.filter(m =>
                m.ingredientName.toLowerCase().includes(searchTerm.toLowerCase())
              ).length
            : merchandiseMovements.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <MerchandiseMovementsTable
            merchandiseMovements={
              searchTerm.trim()
                ? merchandiseMovements.filter(m =>
                    m.ingredientName.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                : merchandiseMovements
            }
            loading={loading}
            onApproveClick={handleApproveClick}
            onRejectClick={handleRejectClick}
            onCancelClick={handleCancelClick}
            user={user}
          />
        </CardContent>
      </Card>

      {/* Modal de formulario */}
      <MerchandiseMovementFormModal
        isOpen={showFormModal}
        onClose={() => {
          setShowFormModal(false);
          setSelectedDirection('all');
        }}
        onSubmit={handleCreateMovement}
        defaultDirection={selectedDirection === 'all' ? 'exit' : selectedDirection}
        ingredients={comboData.ingredients}
        locations={comboData.locations}
        entryTypes={comboData.entryTypes}
        exitTypes={comboData.exitTypes}
      />

      {/* Modal de aprobación */}
      {selectedMovement && (
        <MerchandiseMovementApprovalModal
          movement={selectedMovement}
          isOpen={showApprovalModal}
          onClose={() => {
            setShowApprovalModal(false);
            setSelectedMovement(null);
          }}
          onApproval={handleRejection}
        />
      )}

      {/* Modal de cancelación */}
      {cancelMovement && (
        <MerchandiseMovementApprovalModal
          movement={cancelMovement}
          isOpen={showCancelModal}
          onClose={() => {
            setShowCancelModal(false);
            setCancelMovement(null);
          }}
          onApproval={handleCancellation}
          title={`Cancelar ${cancelMovement.direction === 'entry' ? 'Ingreso' : 'Salida'}`}
          actionText="Cancelar Movimiento"
          reasonLabel="Razón de la cancelación"
          reasonPlaceholder={`Explique por qué se cancela este ${cancelMovement.direction === 'entry' ? 'ingreso' : 'salida'}...${cancelMovement.status === 'approved' ? '\n\n⚠️ ATENCIÓN: Este movimiento está aprobado. Al cancelarlo se revertirá automáticamente el inventario.' : ''}`}
        />
      )}

      {/* Diálogo de confirmación para aprobación */}
      <ConfirmationDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        title="Confirmar Aprobación"
        description={confirmMovement ? `¿Está seguro de aprobar el ${confirmMovement.direction === 'entry' ? 'ingreso' : 'salida'} ${confirmMovement.movementNumber} de ${confirmMovement.ingredientName}?\n\nEsta acción aplicará inmediatamente el movimiento al inventario.` : ''}
        confirmText="Aprobar"
        cancelText="Cancelar"
        onConfirm={confirmApproval}
      />

    </div>
  );
};